import type { App, Field, ObjectType, Pipeline, Relation, Stage, WorkspaceModel } from './metamodel';

export type RelationSide = 'from' | 'to';

/** What a relation field points at, from the perspective of the object that owns the field. */
export type RelationFieldTarget = {
  relation: Relation;
  side: RelationSide;
  /** Object type on the other end. */
  target: ObjectType;
  /** Inverse field on the other end. */
  inverseField: Field;
  /** true when this side holds at most one linked record. */
  single: boolean;
};

/** Is the side of a relation single-valued? deals.company (from, many_to_one) → true. */
export function isSingleSide(cardinality: Relation['cardinality'], side: RelationSide): boolean {
  if (cardinality === 'one_to_one') return true;
  if (cardinality === 'many_to_many') return false;
  return side === 'from' ? cardinality === 'many_to_one' : cardinality === 'one_to_many';
}

/**
 * Indexed, read-only view of a workspace model for fast lookups (server and browser).
 * Archived fields are excluded from key lookups but still resolvable by id.
 */
export class ModelIndex {
  readonly apps: App[];
  readonly objects: ObjectType[];
  readonly relations: Relation[];
  readonly pipelines: Pipeline[];
  readonly #objectsByKey = new Map<string, ObjectType>();
  readonly #objectsById = new Map<string, ObjectType>();
  readonly #fieldsById = new Map<string, Field>();
  readonly #fieldsByStableId = new Map<string, Field>();
  readonly #fieldsByObject = new Map<string, Field[]>();
  readonly #relationsById = new Map<string, Relation>();
  readonly #pipelinesByField = new Map<string, Pipeline[]>();

  readonly #model: WorkspaceModel;

  constructor(model: WorkspaceModel) {
    this.#model = model;
    this.apps = [...model.apps].sort((a, b) => a.position - b.position);
    this.objects = [...model.objects].sort((a, b) => a.position - b.position);
    this.relations = model.relations;
    this.pipelines = [...model.pipelines].sort((a, b) => a.position - b.position);
    for (const o of this.objects) {
      this.#objectsByKey.set(o.key, o);
      this.#objectsById.set(o.id, o);
    }
    for (const r of model.relations) this.#relationsById.set(r.id, r);
    for (const f of [...model.fields].sort((a, b) => a.position - b.position)) {
      this.#fieldsById.set(f.id, f);
      this.#fieldsByStableId.set(f.stableId, f);
      if (f.archivedAt) continue;
      // A relation field whose relation is gone or whose other object is archived has no target: it is not
      // a field of the object while that holds (restoring the object brings it back), so no list, picker,
      // formula or count ever offers a field that leads nowhere.
      if (this.#isOrphanRelation(f)) continue;
      const list = this.#fieldsByObject.get(f.objectTypeId) ?? [];
      list.push(f);
      this.#fieldsByObject.set(f.objectTypeId, list);
    }
    for (const p of this.pipelines) {
      const list = this.#pipelinesByField.get(p.fieldId) ?? [];
      list.push(p);
      this.#pipelinesByField.set(p.fieldId, list);
    }
  }

  /** A relation field whose relation is not live (deleted, or an end object is archived). */
  #isOrphanRelation(field: Field): boolean {
    if (field.type !== 'relation') return false;
    const relationId = field.config.relationId;
    if (typeof relationId !== 'string') return false;
    const relation = this.#relationsById.get(relationId);
    return (
      !relation ||
      !this.#objectsById.has(relation.fromObjectTypeId) ||
      !this.#objectsById.has(relation.toObjectTypeId)
    );
  }

  objectByKey(key: string): ObjectType | undefined {
    return this.#objectsByKey.get(key);
  }
  objectById(id: string): ObjectType | undefined {
    return this.#objectsById.get(id);
  }
  /** Active (non archived) fields of an object, ordered by position. */
  fieldsOf(objectTypeId: string): Field[] {
    return this.#fieldsByObject.get(objectTypeId) ?? [];
  }
  fieldByKey(objectTypeId: string, key: string): Field | undefined {
    return this.fieldsOf(objectTypeId).find((f) => f.key === key);
  }
  fieldById(id: string): Field | undefined {
    return this.#fieldsById.get(id);
  }
  fieldByStableId(stableId: string): Field | undefined {
    return this.#fieldsByStableId.get(stableId);
  }
  fieldByRole(objectTypeId: string, role: Field['role']): Field | undefined {
    return this.fieldsOf(objectTypeId).find((f) => f.role === role);
  }
  titleField(objectTypeId: string): Field | undefined {
    const o = this.objectById(objectTypeId);
    return (
      (o?.titleFieldId ? this.fieldById(o.titleFieldId) : undefined) ??
      this.fieldByRole(objectTypeId, 'title')
    );
  }
  relationById(id: string): Relation | undefined {
    return this.#relationsById.get(id);
  }
  /** Resolve a relation field (type `relation`) to its relation and target object. */
  relationTarget(field: Field): RelationFieldTarget | undefined {
    if (field.type !== 'relation') return undefined;
    const relationId = field.config.relationId;
    const side = field.config.side;
    if (typeof relationId !== 'string' || (side !== 'from' && side !== 'to')) return undefined;
    const relation = this.relationById(relationId);
    if (!relation) return undefined;
    const targetId = side === 'from' ? relation.toObjectTypeId : relation.fromObjectTypeId;
    const target = this.objectById(targetId);
    const inverseField = this.fieldById(side === 'from' ? relation.toFieldId : relation.fromFieldId);
    if (!target || !inverseField) return undefined;
    return { relation, side, target, inverseField, single: isSingleSide(relation.cardinality, side) };
  }
  pipelinesForField(fieldId: string): Pipeline[] {
    return this.#pipelinesByField.get(fieldId) ?? [];
  }
  pipelinesForObject(objectTypeId: string): Pipeline[] {
    return this.pipelines.filter((p) => p.objectTypeId === objectTypeId);
  }
  /** All stages of a status field across its pipelines, ordered by pipeline then stage position. */
  stagesForField(fieldId: string): Stage[] {
    return this.pipelinesForField(fieldId).flatMap((p) =>
      [...p.stages].sort((a, b) => a.position - b.position),
    );
  }
  /**
   * This model with one more object that no table stores (tasks as data): its fields resolve like any
   * object's, so typed components work on it. Nothing is added to the workspace's own model.
   */
  withSyntheticObject(object: ObjectType, fields: Field[]): ModelIndex {
    return new ModelIndex({
      ...this.#model,
      objects: [...this.#model.objects.filter((o) => o.id !== object.id), object],
      fields: [...this.#model.fields.filter((f) => f.objectTypeId !== object.id), ...fields],
    });
  }
  appOf(object: ObjectType): App | undefined {
    return this.apps.find((a) => a.id === object.appId);
  }
}
